import React, { useState, useEffect, useCallback } from "react"
import { ActionCableConsumer } from "react-actioncable-provider"
import { useQueryClient } from "react-query"
import { Spinner } from "../shared/Spinner"
import { RequestError } from "../shared/RequestError"
import { useComments } from "../../hooks/useComments"
import CommentBlock from "../CommentBlock"
import CommentForm from "./CommentForm"
import { Comment, CommentTreeNode } from "../../requests/comments"

interface CommentsData {
  premiere_blurbs: CommentTreeNode[]
  total_all_count: number
  has_next_page: boolean
  comments: CommentTreeNode[]
}

interface CommentSectionProps {
  postId: number
  headerText?: string
  className?: string
}

const CommentSection: React.FC<CommentSectionProps> = ({
  postId,
  headerText = "Comments",
  className = "my-4",
}) => {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [accumulatedTrees, setAccumulatedTrees] = useState<CommentTreeNode[]>([])
  const [pinnedComments, setPinnedComments] = useState<CommentTreeNode[]>([])
  const perPage = 10

  useEffect(() => {
    if (window.location.hash !== "#comments") return
    const timer = setTimeout(() => {
      document.getElementById("comments")?.scrollIntoView({ behavior: "smooth" })
    }, 600)
    return () => clearTimeout(timer)
  }, [])

  const { isFetching, data, error } = useComments(
    postId,
    page,
    perPage,
  ) as { isFetching: boolean; data: CommentsData | undefined; error: unknown }

  const premiereBlurbs = data?.premiere_blurbs ?? []
  const totalAllCount = data?.total_all_count ?? 0
  const hasNextPage = data?.has_next_page ?? false
  const comments = data?.comments

  useEffect(() => {
    if (!comments) return
    if (page === 1) {
      setAccumulatedTrees(comments)
    } else {
      setAccumulatedTrees((prev) => [...prev, ...comments])
    }
    // Pinned comments are a frozen snapshot taken at post-time (see
    // CommentForm's onSuccess below) so they stay put at the top instead of
    // jumping around as the list re-sorts. But that means their own fields
    // (e.g. a video's processing -> ready transition) never picked up later
    // refetches/broadcasts on their own — only their nested replies did, via
    // insertReply. Refresh each pinned comment's data from the latest fetch
    // whenever it's available, keeping the stale snapshot only as a fallback
    // for the (rare) case it's dropped off page 1.
    setPinnedComments((prev) =>
      prev.map((pinned) => comments.find((c) => c.id === pinned.id) ?? pinned),
    )
  }, [comments, page])

  const insertReply = useCallback(
    (node: CommentTreeNode, reply: Comment, parentId: number): CommentTreeNode => {
      if (node.id === parentId) {
        const newNode: CommentTreeNode = { ...reply, replies: [], has_more_replies: false, replies_count: 0 }
        return { ...node, replies: [...node.replies, newNode] }
      }
      return { ...node, replies: node.replies.map((r) => insertReply(r, reply, parentId)) }
    },
    [],
  )

  const handleReplyAdded = useCallback(
    (newReply: Comment, parentId: number) => {
      setAccumulatedTrees((prev) => prev.map((node) => insertReply(node, newReply, parentId)))
      setPinnedComments((prev) => prev.map((node) => insertReply(node, newReply, parentId)))
      queryClient.invalidateQueries(["comments", postId])
    },
    [postId, queryClient, insertReply],
  )

  const handleReceivedMessage = useCallback(
    (payload: { comment?: unknown }) => {
      if (payload.comment) {
        queryClient.invalidateQueries(["comments", postId])
      }
    },
    [postId, queryClient],
  )

  const postChannelDisconnected = (err: unknown) => {
    console.error("PostChannel disconnected: ", err)
  }

  const totalDisplay = premiereBlurbs.length + totalAllCount

  return (
    <div id="comments" className={className}>
      {isFetching && page === 1 && !data ? (
        <Spinner />
      ) : error ? (
        <RequestError error={error} />
      ) : data ? (
        <>
          <ActionCableConsumer
            channel={{ channel: "PostChannel", id: postId }}
            onReceived={handleReceivedMessage}
            onDisconnected={postChannelDisconnected}
          />
          <h4 className="uppercase font-bold text-gray-700 text-xs mb-2">
            {headerText}{" "}
            {totalDisplay > 0 ? (
              <>
                <span className="font-normal">({totalDisplay})</span>
                {hasNextPage ? (
                  <span>
                    <button
                      className="pl-2 text-blue-500 focus:outline-none"
                      onClick={() => setPage((p) => p + 1)}
                    >
                      show more
                    </button>
                  </span>
                ) : null}
              </>
            ) : null}
          </h4>
          <div className="mb-4">
            <CommentForm
              postId={postId}
              placeholder={totalDisplay > 0 ? "Add your comment here" : undefined}
              onSuccess={(newComment) => {
                if (newComment && !newComment.parent_id) {
                  setPinnedComments((prev) => [
                    { ...newComment, replies: [], has_more_replies: false, descendants_count: 0, replies_count: 0 },
                    ...prev,
                  ])
                }
                queryClient.invalidateQueries(["comments", postId])
              }}
            />
          </div>
          <ul>
            {premiereBlurbs.map((comment) => (
              <CommentBlock
                key={`comment-${comment.id}`}
                comment={comment}
                depth={0}
                isTreeNode
                postId={postId}
                postPath={null}
                onReplyAdded={handleReplyAdded}
              />
            ))}
            {pinnedComments.map((node) => (
              <CommentBlock
                key={`pinned-${node.id}`}
                comment={node}
                depth={0}
                isTreeNode
                postId={postId}
                postPath={null}
                onReplyAdded={handleReplyAdded}
                highlighted
              />
            ))}
            {accumulatedTrees
              .filter((node) => !pinnedComments.some((p) => p.id === node.id))
              .map((node) => (
                <CommentBlock
                  key={`comment-${node.id}`}
                  comment={node}
                  depth={0}
                  isTreeNode
                  postId={postId}
                  postPath={null}
                  onReplyAdded={handleReplyAdded}
                />
              ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}

export default CommentSection
